import { Component, OnInit, OnDestroy } from '@angular/core';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { takeUntil, map, combineLatestWith } from 'rxjs/operators';

import { TokenMonitorService, TokenStatus } from '@common/services/token-monitor.service';

@Component({
    selector: 'aa-token-warning-banner',
    templateUrl: './token-warning-banner.component.html',
    styleUrls: ['./token-warning-banner.component.scss'],
    standalone: false
})
export class TokenWarningBannerComponent implements OnInit, OnDestroy {
    private destroyator = new Subject();
    private dismissedSubject = new BehaviorSubject<boolean>(false);
    public tokenStatus$: Observable<TokenStatus>;
    public expirationTimeFormatted$: Observable<string>;
    public shouldShow$: Observable<boolean>;

    constructor(private tokenMonitorService: TokenMonitorService) {}

    ngOnInit(): void {
        this.tokenStatus$ = this.tokenMonitorService.tokenStatus$;
        
        this.expirationTimeFormatted$ = this.tokenStatus$.pipe(
            map(status => this.formatExpirationTime(status.expirationDate)),
            takeUntil(this.destroyator)
        );

        // Combine token status with dismissal state to determine visibility
        this.shouldShow$ = this.tokenStatus$.pipe(
            combineLatestWith(this.dismissedSubject),
            map(([status, isDismissed]) => (status.isExpiringSoon || status.isExpired) && !isDismissed),
            takeUntil(this.destroyator)
        );

        // Reset dismissal state when token is refreshed or expires
        this.tokenStatus$.pipe(
            takeUntil(this.destroyator)
        ).subscribe(status => {
            if (!status.isExpiringSoon && !status.isExpired) {
                this.dismissedSubject.next(false);
            }
        });
    }

    ngOnDestroy(): void {
        this.destroyator.next(true);
        this.destroyator.complete();
    }
    
    public handleSessionAction(): void {
        this.tokenMonitorService.handleSessionAction();
    }

    public refreshToken(): void {
        this.tokenMonitorService.refreshToken().subscribe(success => {
            if (success) {
                this.dismissedSubject.next(false); // Reset dismissal when refreshing successfully
            }
        });
    }

    public dismissBanner(): void {
        this.dismissedSubject.next(true);
    }

    private formatExpirationTime(expirationDate: Date | null): string {
        if (!expirationDate) {
            return '';
        }

        // Format the expiration date to local time
        return expirationDate.toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    }
}
